// The review workspace: the games open on the review screen as board tabs.
// The first tab is pinned and is the game the address names; more tabs are
// opened beside it to compare (the opening history opens them). Each tab is
// a `ReviewSession` of its own, keeping its own ply, line and look back, so
// switching tabs loses nothing.
//
// The workspace is what the `review` API is: it stands in for the active
// session, so every panel reading `state()`, `subscribe` or `select`, and
// calling `go`, `play` and the rest, follows whichever tab is showing
// without knowing tabs exist. Switching tabs looks to a panel like the
// review changing game. The tab strip reads `tabs()` and `selectTabs`.
import type { MoveInput } from '@chess/api-chess';
import { Observable } from './observable.ts';
import { ReviewSession, type ReviewServices } from './session.ts';
import type { GameLoader, Review, ReviewRules, ReviewState } from './index.ts';
import { type San } from '@chess/api-model';

/** One board tab. */
export interface ReviewTab {
  /** Stable for the tab's life; the pinned tab's is `"pinned"`. */
  id: string;
  /** The game the tab shows; undefined for a pinned tab with no game open. */
  gameId?: string;
  /** The first tab: the game the address names. It cannot be closed. */
  pinned: boolean;
  /** What the opener says about the game, shown after its date, e.g. its opening score "−2.8". */
  caption?: string;
}

export interface ReviewTabs {
  tabs: ReviewTab[];
  activeId: string;
}

/** Unpinned tabs at most; opening one more closes the oldest. */
export const MAX_EXTRA_TABS = 5;

const PINNED = 'pinned';

/** The tabs as their own observable state, beside the active session's. */
class TabsState extends Observable<ReviewTabs> {
  set(patch: Partial<ReviewTabs>): void {
    this.update(patch);
  }
}

export class ReviewWorkspace extends Observable<ReviewState> implements Review {
  private readonly load: GameLoader;
  private readonly rules?: ReviewRules;
  private services: ReviewServices;
  private readonly sessions = new Map<string, ReviewSession>();
  private readonly tabsState: TabsState;
  private unfollow?: () => void;
  private nextId = 1;

  constructor(load: GameLoader, rules?: ReviewRules, services: ReviewServices = {}) {
    const pinned = new ReviewSession(load, rules, services, PINNED);
    super(pinned.state());
    this.load = load;
    this.rules = rules;
    this.services = services;
    this.sessions.set(PINNED, pinned);
    this.tabsState = new TabsState({ tabs: [{ id: PINNED, pinned: true }], activeId: PINNED });
    this.follow(pinned);
  }

  // ---- the tabs ------------------------------------------------------------

  tabs(): ReviewTabs {
    return this.tabsState.state();
  }

  /** Follow the tabs; returns the unsubscribe. */
  subscribeTabs(listener: (tabs: ReviewTabs) => void): () => void {
    return this.tabsState.subscribe(listener);
  }

  /** The session behind a tab, e.g. for its game's label. */
  sessionOf(tabId: string): ReviewSession | undefined {
    return this.sessions.get(tabId);
  }

  /** The session showing now. */
  active(): ReviewSession {
    return this.sessions.get(this.tabs().activeId) ?? this.sessions.get(PINNED)!;
  }

  /**
   * Show a game in a tab at a ply: switches to the tab already showing it,
   * the pinned one included, or opens a new tab beside the others.
   */
  async openTab(gameId: string, ply?: number, caption?: string): Promise<void> {
    const existing = this.tabs().tabs.find((t) => this.sessions.get(t.id)?.state().gameId === gameId);
    if (existing) {
      this.activate(existing.id);
      if (ply !== undefined) this.sessions.get(existing.id)?.go(ply);
      return;
    }
    const id = `tab-${this.nextId++}`;
    const session = new ReviewSession(this.load, this.rules, this.services, id);
    this.sessions.set(id, session);
    let tabs = [...this.tabs().tabs, { id, gameId, pinned: false, caption }];
    const extra = tabs.filter((t) => !t.pinned);
    if (extra.length > MAX_EXTRA_TABS) {
      const oldest = extra[0];
      tabs = tabs.filter((t) => t.id !== oldest.id);
      this.dispose(oldest.id);
    }
    this.tabsState.set({ tabs });
    this.activate(id);
    await session.open(gameId);
    if (ply !== undefined && session.state().gameId === gameId) session.go(ply);
  }

  /** Show a tab. */
  activate(tabId: string): void {
    const session = this.sessions.get(tabId);
    if (!session || this.tabs().activeId === tabId) return;
    this.tabsState.set({ activeId: tabId });
    this.follow(session);
  }

  /** Close an unpinned tab; the one before it shows if it was active. */
  close(tabId: string): void {
    const tabs = this.tabs().tabs;
    const at = tabs.findIndex((t) => t.id === tabId);
    if (at < 0 || tabs[at].pinned) return;
    const rest = tabs.filter((t) => t.id !== tabId);
    const wasActive = this.tabs().activeId === tabId;
    this.tabsState.set({ tabs: rest });
    if (wasActive) this.activate(rest[Math.max(0, at - 1)].id);
    this.dispose(tabId);
  }

  // ---- services ------------------------------------------------------------

  connect(services: ReviewServices): void {
    this.services = { ...this.services, ...services };
    for (const session of this.sessions.values()) session.connect(services);
  }

  // ---- the Review interface, on the active session ----------------------------

  /**
   * Open a game in the pinned tab: the game the address names. Every other
   * tab closes, since a new game starts a new comparison, and every
   * constraint on the pinned board is released.
   */
  async open(gameId: string | undefined): Promise<void> {
    for (const tab of this.tabs().tabs) if (!tab.pinned) this.dispose(tab.id);
    this.tabsState.set({ tabs: [{ id: PINNED, gameId, pinned: true }], activeId: PINNED });
    const pinned = this.sessions.get(PINNED)!;
    // Opened by the address (arriving on the screen, a link, a reload): the
    // board starts free, even on the game it already shows. A puzzle left
    // running when the reader went elsewhere ends here, so it cannot keep
    // the board's arrows and the other giveaways hidden.
    pinned.releaseConstraints();
    this.follow(pinned);
    await pinned.open(gameId);
  }

  go(ply: number): void {
    this.active().go(ply);
  }
  stepBackward(): void {
    this.active().stepBackward();
  }
  stepForward(): void {
    this.active().stepForward();
  }
  toStart(): void {
    this.active().toStart();
  }
  toEnd(): void {
    this.active().toEnd();
  }
  play(move: MoveInput | San): boolean {
    return this.active().play(move);
  }
  returnToGame(): void {
    this.active().returnToGame();
  }
  browse(ply: number | undefined): void {
    this.active().browse(ply);
  }

  // ---- internals -----------------------------------------------------------

  /** Mirror a session's state as this object's, from now on. */
  private follow(session: ReviewSession): void {
    this.unfollow?.();
    this.unfollow = session.subscribe((state) => {
      if (this.active() === session) this.replace(state);
    });
    this.replace(session.state());
  }

  private dispose(tabId: string): void {
    if (tabId === PINNED) return;
    const session = this.sessions.get(tabId);
    this.sessions.delete(tabId);
    session?.releaseConstraints();
    // Opening nothing stops the session's scan subscription.
    void session?.open(undefined);
  }
}

/** A workspace whose pinned tab opens games through `load`. */
export function createWorkspace(load: GameLoader, rules?: ReviewRules, services?: ReviewServices): ReviewWorkspace {
  return new ReviewWorkspace(load, rules, services);
}
